<template>
  <div>
    <div class="flex min-w-0 w-full items-center gap-2">
      <o-button
        transparent
        icon="chevronLeft"
        size="sm"
        class="h-9 shrink-0"
        :icon-size="10"
        @click="move(-1)"
      />

      <date-range-optional-field
        v-model="_value"
        :status="status"
        :limit="unit === 'custom'"
        stacked
        class="min-w-0 flex-1"
        @changeStart="handleStartChange"
        @changeFinish="handleFinishChange"
      />

      <o-button
        transparent
        icon="chevronRight"
        size="sm"
        class="h-9 shrink-0"
        :icon-size="10"
        @click="move(1)"
      />
    </div>

    <div class="mt-3">
      <o-chip
        v-for="option in units"
        :key="option.label"
        class="mb-1 mr-1 cursor-pointer"
        :variant="unit === option.value ? 'primary' : 'default'"
        :flat="unit === option.value"
        @click="unit = option.value"
      >
        {{ option.label }}
      </o-chip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useNuxtApp } from '#app'

import {
  DateRangeOptionalField,
} from '@/components/fields'

const { $date, $dateFns, $i18n } = useNuxtApp()

interface Props {
  modelValue: Record<string, any>
  status?: string | boolean | null
}

const props = withDefaults(defineProps<Props>(), {
  status: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, any>): void
}>()

const unit = ref('months')

const _value = computed({
  get () {
    return props.modelValue
  },
  set (val: Record<string, any>) {
    emit('update:modelValue', val)
  },
})

const units = computed(() => [
  {
    label: $i18n.t('datetime.range.daily'),
    value: 'days',
  },
  {
    label: $i18n.t('datetime.range.weekly'),
    value: 'weeks',
  },
  {
    label: $i18n.t('datetime.range.monthly'),
    value: 'months',
  },
  {
    label: $i18n.t('datetime.range.quarterly'),
    value: 'quarter',
  },
  {
    label: $i18n.t('datetime.range.yearly'),
    value: 'years',
  },
  {
    label: $i18n.t('general.custom'),
    value: 'custom',
  },
])

watch(unit, (u) => {
  if (u !== 'custom') {
    const start = _value.value.start * 1000
    const date = $date.startOf(start, u)
    const value = 0

    set(date, u, value)
  }
})

function move (value: number) {
  const start = _value.value.start * 1000
  const finish = _value.value.finish * 1000
  const u = unit.value

  // Custom unit type uses days to increment date
  const _unit = u === 'custom' ? 'days' : u

  // Increment date by current different between start and finish dates if customer unit type
  const _val = u === 'custom' ? (($dateFns.differenceInDays(finish, start) + 1) * value) : value

  set(start, _unit, _val)
}

function set (date: any, u: string, value: number) {
  const _unit = u === 'quarter' ? 'months' : u
  const duration: Record<string, any> = {}

  // Increment date by unit type and value
  duration[_unit] = value
  const start = $date.add(date, duration)

  // Increment date by twice the unit type and value, less one day
  let amount = value + (Math.abs(value) > 1 ? value - 1 : 1)

  if (u === 'quarter') {
    amount = amount * 3
  }

  duration[_unit] = amount
  duration.days = duration.days || -1

  const finish = unit.value === 'days' ? start : $date.add(date, duration)

  _value.value = {
    start: Math.round(start.getTime() / 1000),
    finish: Math.round($dateFns.endOfDay(finish).getTime() / 1000) - 1,
  }
}

function handleStartChange () {
  const u = unit.value
  const date = _value.value.start

  set(date * 1000, u, 0)
}

function handleFinishChange (value: any) {
  if (value.old !== value.new) {
    unit.value = 'custom'
  }
}
</script>
